import { Box, Typography, ToggleButtonGroup, ToggleButton } from "@mui/material";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import DrawIcon from "@mui/icons-material/Draw";
import StraightenIcon from "@mui/icons-material/Straighten";

// Which tabs the take page shows when the author has said nothing (allowsDrawAnswer in
// StudentPaperTake.jsx): Draw is on for drawing and short-answer questions, off everywhere else. The
// editors seed their toggles from this so opening a question of any other type and saving it cannot
// switch a Draw tab on that the student never had.
export const drawTabDefaultForType = (type) => {
  const t = String(type || "").toLowerCase().replace(/_/g, "-");
  return t === "drawing" || t === "short-answer";
};

/**
 * Author control that mirrors student answer tabs.
 * Selected = enabled for the student; deselected = hidden/disabled.
 * At least one tab must stay enabled.
 */
export default function StudentAnswerTabsEnable({
  allowTypeAnswer,
  allowDrawAnswer,
  allowConstructBoard = false,
  showConstructOption = false,
  onChange,
  caption = "Student answer tabs (same as on the take page). Toggle to enable or disable.",
}) {
  const selected = [];
  if (allowTypeAnswer) selected.push("type");
  if (allowDrawAnswer) selected.push("draw");
  if (showConstructOption && allowConstructBoard) selected.push("construct");

  const handleChange = (_e, next) => {
    const nextArr = Array.isArray(next) ? next : [];
    let typeOn = nextArr.includes("type");
    let drawOn = nextArr.includes("draw");
    let constructOn = showConstructOption ? nextArr.includes("construct") : false;
    if (!typeOn && !drawOn && !constructOn) {
      typeOn = true;
    }
    onChange({
      allowTypeAnswer: typeOn,
      allowDrawAnswer: drawOn,
      ...(showConstructOption ? { allowConstructBoard: constructOn } : {}),
    });
  };

  const summary = (() => {
    const parts = [];
    if (allowTypeAnswer) parts.push("Type");
    if (allowDrawAnswer) parts.push("Draw");
    if (showConstructOption && allowConstructBoard) parts.push("Construction Board");
    if (parts.length === 3) return "Student sees all three tabs.";
    if (parts.length === 2) return `Student sees ${parts.join(" + ")}.`;
    if (parts[0] === "Type") return "Student sees Type Answer only.";
    if (parts[0] === "Draw") return "Student sees Draw Answer only.";
    return "Student sees Construction Board only.";
  })();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
      <Typography variant="caption" color="text.secondary">
        {caption}
      </Typography>
      <ToggleButtonGroup
        value={selected}
        onChange={handleChange}
        size="small"
        sx={{
          alignSelf: "flex-start",
          flexWrap: "wrap",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          "& .MuiToggleButton-root": {
            textTransform: "none",
            fontWeight: 600,
            px: 1.5,
            py: 0.75,
            gap: 0.5,
          },
        }}
      >
        <ToggleButton value="type" aria-label="Enable Type Answer tab">
          <KeyboardIcon sx={{ fontSize: 16 }} />
          Type Answer
        </ToggleButton>
        <ToggleButton value="draw" aria-label="Enable Draw Answer tab">
          <DrawIcon sx={{ fontSize: 16 }} />
          Draw Answer
        </ToggleButton>
        {showConstructOption ? (
          <ToggleButton value="construct" aria-label="Enable Construction Board tab">
            <StraightenIcon sx={{ fontSize: 16 }} />
            Construction Board
          </ToggleButton>
        ) : null}
      </ToggleButtonGroup>
      <Typography variant="caption" color="text.secondary">
        {summary}
      </Typography>
    </Box>
  );
}
